import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUI } from '@/hooks/ui/useUI';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { workingDayService } from '@/api/domains/workingDay';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { workingDayTypeCatalogService, WORKING_DAY_TYPE_ID } from '@/api/domains/catalog/workingDayType';
import { progressivityService } from '@/api/domains/attendance';
import { shiftService } from '@/api/domains/shift';
import type { LC_ShiftDayDetailIn, LC_UpdateCompanyShiftIn } from '@/api/domains/shift';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  STATIC_WORKING_DAY_TYPES,
  type CreateShiftDescriptionForm,
} from '@/schemas/shift/createShiftDescriptionForm';
import {
  updateShiftDescriptionSchema,
  type UpdateShiftDescriptionForm,
  type UpdateShiftDescriptionFormSubset,
} from '@/schemas/shift/updateShiftDescriptionForm';


const WORKING_DAY_CATALOG_PAGE_SIZE = 1000;
const PARTIAL_WORKING_DAY_ORDINARY_HOURS = 30;
const DAYS_PER_WEEK = 7;
// ASIS-5053 (QA): mismo bloqueo que useShiftCreate.ts — Bisemanal también queda fijo,
// en 2 semanas, para que la grilla siempre se construya sobre exactamente 14 días.
const LOCKED_WEEKS_COUNT_BY_TYPE: Record<number, number> = {
  [WORKING_DAY_TYPE_ID.FIXED]: 1,
  [WORKING_DAY_TYPE_ID.PARTIAL]: 1,
  [WORKING_DAY_TYPE_ID.BIWEEKLY]: 2,
};

const DEFAULT_VALUES: DefaultValues<UpdateShiftDescriptionForm> = {
  workingDayTypeId: null,
  workingDayId: null,
  shiftName: '',
  weeksCount: 1,
  isExceptionalWorkingDay: false,
  dtAuthorizationNumber: '',
  dtAuthorizationDate: '',
  staticStartDate: '',
};

export interface UseShiftEditResult {
  formSubset: UpdateShiftDescriptionFormSubset;
  shiftId: number | undefined;
  shiftCode: string;
  isLoadingCatalogs: boolean;
  isDirty: boolean;
  workingDayTypeOptions: SelectorOption[];
  workingDayOptions: SelectorOption[];
  selectedWorkingDayTypeId: number | null;
  selectedWorkingDay: WorkingDayItem | undefined;
  isDistributedCyclesType: boolean;
  isExceptionalType: boolean;
  isStaticType: boolean;
  ordinaryHours: number | null;
  weeksCountEditable: boolean;
  /** true when the shift is assigned (hasAssignments) OR the user lacks edit permission. */
  isReadOnly: boolean;
  hasAssignments: boolean;
  canEdit: boolean;
  /** Snapshot of the shift's day-distribution, used to preload the weekly grid. */
  initialDayDetails: LC_ShiftDayDetailIn[];
  /**
   * Current description values shaped for `useShiftWeeklyDistribution`, which shares
   * the Create form's type. `shiftCode` is filled in with the read-only original code:
   * the grid only reads it on Create's POST path, unreachable here.
   */
  descriptionValues: CreateShiftDescriptionForm;
  isEnabled: boolean;
  isTogglingStatus: boolean;
  onToggleStatus: () => void;
  isSaving: boolean;
  /** Receives the grid's current day assignments (wired as its `onSaveOverride`). */
  onSave: (details: LC_ShiftDayDetailIn[]) => Promise<void>;
  handleCancel: () => void;
  confirmNavigation: () => void;
}

export function useShiftEdit(): UseShiftEditResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification, showSuccess, showApiError, openModal, closeModal } = useUI();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { createSignal } = useAbortController();
  // NOTE: auto-resolves screenCode from the current URL (matches ScheduleEdit /
  // CreateShiftDescriptionForm). Passing SECTIONS.TURNOS_ROTATIVOS_Y_FIJOS as an
  // explicit screenCode override here — as useShiftCatalogList.tsx currently does —
  // would look up permissionsMap['TURNOS_ROTATIVOS_Y_FIJOS'], which never matches
  // a route code (e.g. '5.2.5') and always resolves to deny-all. Flagged for review.
  const { canEdit } = usePagePermissions();

  const numericId = id !== undefined ? Number(id) : NaN;
  const shiftId = !Number.isNaN(numericId) ? numericId : undefined;

  const [isLoadingShift, setIsLoadingShift] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [shiftCode, setShiftCode] = useState('');
  const [hasAssignments, setHasAssignments] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [initialDayDetails, setInitialDayDetails] = useState<LC_ShiftDayDetailIn[]>([]);

  const {
    register,
    reset,
    watch,
    setValue,
    setError,
    trigger,
    formState: { errors, isDirty },
  } = useForm<UpdateShiftDescriptionForm>({
    resolver: zodResolver(updateShiftDescriptionSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const formSubset: UpdateShiftDescriptionFormSubset = {
    register, setValue, watch, errors, trigger,
  };

  // ── Load shift detail ────────────────────────────────────────────────────
  const loadShift = useCallback(async () => {
    if (!shiftId) {
      setIsLoadingShift(false);
      return;
    }

    setIsLoadingShift(true);
    const signal = createSignal();
    const response = await shiftService.getCompanyShiftById(shiftId, signal);

    if (response.error?.code === 'ABORTED') return;

    if (!response.success || !response.data) {
      showApiError(response, translate('shift.edit.messages.loadError'));
      setIsLoadingShift(false);
      return;
    }

    const data = response.data;
    setShiftCode(data.shiftCode);
    setHasAssignments(data.hasAssignments);
    setIsEnabled(data.isEnabled);
    setInitialDayDetails(data.details);

    // Preload the form either way — when `hasAssignments` is true the fields are
    // rendered disabled (read-only) but must still show the shift's real data.
    reset({
      workingDayTypeId: data.workingDayTypeId,
      workingDayId: data.workingDayId,
      shiftName: data.shiftName,
      weeksCount: data.weeksCount,
      isExceptionalWorkingDay: data.isExceptionalWorkingDay,
      dtAuthorizationNumber: data.dtAuthorizationNumber,
      dtAuthorizationDate: data.dtAuthorizationDate,
      staticStartDate: data.staticStartDate,
    });

    setIsLoadingShift(false);
  }, [shiftId, createSignal, reset, showApiError, translate]);

  useEffect(() => {
    void loadShift();
  }, [loadShift]);

  // ── Catálogos: mismos endpoints que Create (ASIS-5049) ──────────────────
  const {
    options: workingDayTypeOptions,
    isLoaded: isTypesLoaded,
  } = useCatalogLoader(
    (signal) => workingDayTypeCatalogService.getWorkingDayTypes(signal),
    (item) => ({ value: String(item.id), label: item.description }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: workingDays, isLoaded: isWorkingDaysLoaded } = useCatalogLoader(
    async (signal) => {
      const response = await workingDayService.list(
        { pageSize: WORKING_DAY_CATALOG_PAGE_SIZE },
        signal,
      );
      if (!response.success || !response.data) return { ...response, data: null };
      return { ...response, data: response.data.items };
    },
    (item: WorkingDayItem) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: progressivities, isLoaded: isProgressivitiesLoaded } = useCatalogLoader(
    () => progressivityService.getProgressivities(),
    (item) => ({ value: item.startDate, label: String(item.weeklyHours) }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const isLoadingCatalogs =
    !isTypesLoaded || !isWorkingDaysLoaded || !isProgressivitiesLoaded || isLoadingShift;

  const selectedWorkingDayTypeId = watch('workingDayTypeId');
  const selectedWorkingDayId = watch('workingDayId');

  const isDistributedCyclesType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.DISTRIBUTED_CYCLES;
  const isExceptionalType = selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.EXCEPTIONAL;
  const isStaticType =
    selectedWorkingDayTypeId !== null &&
    STATIC_WORKING_DAY_TYPES.includes(selectedWorkingDayTypeId);
  const weeksCountEditable =
    selectedWorkingDayTypeId === null ||
    !(selectedWorkingDayTypeId in LOCKED_WEEKS_COUNT_BY_TYPE);

  const workingDayOptions = useMemo<SelectorOption[]>(() => {
    const enabledWorkingDays = workingDays.filter((workingDay) => workingDay.status === 'enabled');
    const filtered = selectedWorkingDayTypeId !== null
      ? enabledWorkingDays.filter(
        (workingDay) => workingDay.workingDayTypeId === selectedWorkingDayTypeId,
      )
      : enabledWorkingDays;
    return filtered.map((workingDay) => ({ value: String(workingDay.id), label: workingDay.name }));
  }, [workingDays, selectedWorkingDayTypeId]);

  const selectedWorkingDay = useMemo(
    () => workingDays.find((workingDay) => workingDay.id === selectedWorkingDayId),
    [workingDays, selectedWorkingDayId],
  );

  const currentWeeklyHours = useMemo(() => {
    const today = new Date().getTime();
    const applicable = progressivities
      .filter((progressivity) => new Date(progressivity.rawStartDate).getTime() <= today)
      .sort((left, right) =>
        new Date(right.rawStartDate).getTime() - new Date(left.rawStartDate).getTime(),
      );
    return applicable[0]?.weeklyHours ?? null;
  }, [progressivities]);

  const isFixedOrByShiftType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.FIXED ||
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BY_SHIFT;

  const ordinaryHours = useMemo(() => {
    if (isFixedOrByShiftType) {
      return currentWeeklyHours;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.PARTIAL) {
      return PARTIAL_WORKING_DAY_ORDINARY_HOURS;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BIWEEKLY) {
      const workingDaysPattern = selectedWorkingDay?.workingDays;
      const offDaysPattern = selectedWorkingDay?.offDays;
      if (!workingDaysPattern || !offDaysPattern || currentWeeklyHours === null) {
        return null;
      }
      const totalCycleDays = workingDaysPattern + offDaysPattern;
      return (totalCycleDays / DAYS_PER_WEEK) * currentWeeklyHours;
    }
    if (isDistributedCyclesType || isExceptionalType) {
      return selectedWorkingDay?.ordinaryWeeklyHours ?? null;
    }
    return null;
  }, [
    isFixedOrByShiftType,
    selectedWorkingDayTypeId,
    currentWeeklyHours,
    isDistributedCyclesType,
    isExceptionalType,
    selectedWorkingDay,
  ]);

  // ── Read-only gating: assigned shifts OR missing edit permission ────────
  const isReadOnly = hasAssignments || !canEdit;

  // Valores de descripción con la forma que consume useShiftWeeklyDistribution
  // (comparte el tipo del formulario de creación), ya listos para la página.
  const descriptionValues: CreateShiftDescriptionForm = { ...watch(), shiftCode };

  // ── Navegación ───────────────────────────────────────────────────────────
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.path);
  }, [navigate]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  // ── Guardar (PUT /v1/attendance/shifts/{id}) ────────────────────────────
  // Se cablea como `onSaveOverride` de useShiftWeeklyDistribution: la grilla invoca
  // este handler pasándole la distribución de días vigente, de modo que el guardado
  // combinado (descripción + distribución) queda centralizado acá.
  const onSave = useCallback(async (details: LC_ShiftDayDetailIn[]): Promise<void> => {
    if (isReadOnly || !shiftId) return;

    const isValid = await trigger();
    if (!isValid) {
      showNotification('error', translate('shift.create.messages.validationError'));
      return;
    }

    const values = watch();
    const payload: LC_UpdateCompanyShiftIn = {
      workingDayTypeId: values.workingDayTypeId as number,
      workingDayId: values.workingDayId as number,
      shiftName: values.shiftName,
      weeksCount: values.weeksCount,
      isExceptionalWorkingDay: values.isExceptionalWorkingDay,
      dtAuthorizationNumber: values.dtAuthorizationNumber || undefined,
      dtAuthorizationDate: values.dtAuthorizationDate || undefined,
      staticStartDate: values.staticStartDate || undefined,
      details,
    };

    setIsSaving(true);
    try {
      const response = await shiftService.updateCompanyShift(shiftId, payload);

      if (!response.success) {
        if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
          return;
        }
        showApiError(response, translate('shift.edit.messages.updateError'));
        return;
      }

      showSuccess(translate('shift.edit.messages.updateSuccess'));
      confirmNavigation();
      navigateToList();
    } finally {
      setIsSaving(false);
    }
  }, [
    isReadOnly,
    shiftId,
    trigger,
    watch,
    setError,
    showNotification,
    showApiError,
    showSuccess,
    translate,
    confirmNavigation,
    navigateToList,
  ]);

  // ── Toggle Activo/Inactivo (PATCH /v1/attendance/shifts/{id}/status) ────
  // Independent from the edit form — available even when the shift is read-only
  // (assigned); never gated by `isReadOnly`.
  const onToggleStatus = useCallback(() => {
    if (!shiftId) return;
    const nextValue = !isEnabled;

    openModal('ConfirmModal', {
      variant: 'alert',
      icon: 'alert',
      title: translate('shift.edit.messages.toggleStatusConfirmTitle'),
      bodyText: translate('shift.edit.messages.toggleStatusConfirmBody'),
      onConfirm: async () => {
        setIsTogglingStatus(true);
        const response = await shiftService.toggleCompanyShiftStatus(shiftId, nextValue);
        setIsTogglingStatus(false);

        if (!response.success) {
          showApiError(response, translate('shift.edit.messages.toggleStatusError'));
          closeModal('ConfirmModal');
          return;
        }

        setIsEnabled(nextValue);
        showSuccess(translate('shift.edit.messages.toggleStatusSuccess'));
        closeModal('ConfirmModal');
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [shiftId, isEnabled, openModal, closeModal, showApiError, showSuccess, translate]);

  return {
    formSubset,
    shiftId,
    shiftCode,
    isLoadingCatalogs,
    isDirty,
    workingDayTypeOptions,
    workingDayOptions,
    selectedWorkingDayTypeId,
    selectedWorkingDay,
    isDistributedCyclesType,
    isExceptionalType,
    isStaticType,
    ordinaryHours,
    weeksCountEditable,
    isReadOnly,
    hasAssignments,
    canEdit,
    initialDayDetails,
    descriptionValues,
    isEnabled,
    isTogglingStatus,
    onToggleStatus,
    isSaving,
    onSave,
    handleCancel,
    confirmNavigation,
  };
}

export default useShiftEdit;
